/**
 * ScrollPreserver.ts
 * Prevents unwanted scroll-to-top jumps (`window.scrollY = 0` or `container.scrollTop = 0`)
 * when interactive UI buttons, county diorama controls, or modal actions re-render DOM subtrees via `innerHTML`.
 */

export interface ScrollSnapshotEntry {
  top: number;
  left: number;
}

export interface PreserveScrollOptions {
  /** Optional selectors whose scrollTop should intentionally be reset instead of restored */
  resetSelectors?: string[];
  /**
   * Set to true when opening a modal via `.open()` so window/background scroll is preserved
   * while the newly opened modal's internal scroll containers start cleanly at 0.
   */
  isFreshModalOpen?: boolean;
}

const KNOWN_SCROLLABLE_SELECTORS: readonly string[] = [
  '.state-inspect-card',
  '.career-modal-card',
  '.pantry-modal-card',
  '.decor-catalog-card',
  '.shortcuts-modal-card',
  '.ui-modal-card',
  '#modal-portal',
  '#modal-container',
  '#tab-content-body',
  '#state-dossier-panel',
  '.dossier-column',
  '#ca-counties-explorer',
  '#ca-county-detail-card',
  '.gpgp-county-dossier-card',
  '.gpgp-neighbors-chips-wrap',
  '.decor-shop-filter-bar',
  '.decor-modal-preview-frame',
  '#scenario-log-console',
  '#ig-active-state-card',
  '.ig-gps-ribbon',
  '.gps-preset-ribbon',
  '#decor-floating-dock',
  '.decor-quick-actions-row'
];

let pendingRafEntries: Array<{ win: Window & typeof globalThis; id: number }> = [];

/**
 * Cancels any pending post-frame scroll restoration callbacks so stale snapshots
 * never overwrite subsequent modal transitions or intentional scroll resets.
 */
export function cancelPendingScrollRestores(): void {
  for (const entry of pendingRafEntries) {
    try {
      if (typeof entry.win.cancelAnimationFrame === 'function') {
        entry.win.cancelAnimationFrame(entry.id);
      }
    } catch {
      // Ignore cancellation errors in synthetic DOMs
    }
  }
  pendingRafEntries = [];
}

/**
 * Captures all current window, document, ancestor, and inner container scroll offsets
 * (plus `<details id="...">` open states) before running `mutateFn`, and restores them
 * synchronously (with a tracked post-frame guard against browser focus/layout clamping).
 */
export function preserveScrollPositions(
  scopeEl: HTMLElement | Document | null | undefined,
  mutateFn: () => void,
  options?: PreserveScrollOptions
): void {
  cancelPendingScrollRestores();

  const doc: Document | null =
    typeof document !== 'undefined'
      ? document
      : scopeEl && 'documentElement' in scopeEl
      ? (scopeEl as Document)
      : scopeEl instanceof HTMLElement
      ? scopeEl.ownerDocument
      : null;

  const win: (Window & typeof globalThis) | null =
    typeof window !== 'undefined'
      ? window
      : (doc?.defaultView as (Window & typeof globalThis) | null) ?? null;

  const isFreshModalOpen = Boolean(options?.isFreshModalOpen);
  const resetSet = new Set(options?.resetSelectors ?? []);

  const initialHostEl =
    scopeEl && 'nodeType' in scopeEl && scopeEl.nodeType === 1 ? (scopeEl as HTMLElement) : null;
  const scopeId = initialHostEl?.id || null;

  const modalContainerEl = doc?.getElementById('modal-container') ?? null;
  const modalPortalEl = doc?.getElementById('modal-portal') ?? null;

  const isInsideModalScope = (el: Element | null | undefined): boolean => {
    if (!el) return false;
    if (modalContainerEl && (el === modalContainerEl || modalContainerEl.contains(el))) return true;
    if (modalPortalEl && (el === modalPortalEl || modalPortalEl.contains(el))) return true;
    if (isFreshModalOpen && initialHostEl && (el === initialHostEl || initialHostEl.contains(el))) {
      return true;
    }
    return false;
  };

  const getLiveScopeEl = (): HTMLElement | null => {
    if (!initialHostEl) return null;
    if (initialHostEl.isConnected !== false) return initialHostEl;
    if (scopeId && doc) {
      return doc.getElementById(scopeId);
    }
    return null;
  };

  // 1. Snapshot Window & Document scroll positions
  const docElTop = doc?.documentElement ? doc.documentElement.scrollTop : 0;
  const docElLeft = doc?.documentElement ? doc.documentElement.scrollLeft : 0;
  const bodyTop = doc?.body ? doc.body.scrollTop : 0;
  const bodyLeft = doc?.body ? doc.body.scrollLeft : 0;
  const scrollingElTop = doc?.scrollingElement ? doc.scrollingElement.scrollTop : 0;
  const scrollingElLeft = doc?.scrollingElement ? doc.scrollingElement.scrollLeft : 0;
  const winY = win ? win.scrollY || win.pageYOffset || docElTop || bodyTop || 0 : docElTop;
  const winX = win ? win.scrollX || win.pageXOffset || docElLeft || bodyLeft || 0 : docElLeft;

  // 2. Snapshot direct ancestor chain of scopeEl
  const directElements = new Map<HTMLElement, ScrollSnapshotEntry>();
  let curr: HTMLElement | null = initialHostEl;
  while (curr) {
    if (!(isFreshModalOpen && isInsideModalScope(curr))) {
      directElements.set(curr, { top: curr.scrollTop, left: curr.scrollLeft });
    }
    curr = curr.parentElement;
  }

  // 3. Snapshot known scrollable containers by selector and by ID
  const bySelector = new Map<string, ScrollSnapshotEntry>();
  const byId = new Map<string, ScrollSnapshotEntry>();
  const detailsOpenById = new Map<string, boolean>();

  const queryTarget = (selector: string): HTMLElement | null => {
    const liveScope = getLiveScopeEl();
    if (liveScope && 'querySelector' in liveScope) {
      const insideScope = liveScope.querySelector<HTMLElement>(selector);
      if (insideScope) return insideScope;
    }
    return doc ? doc.querySelector<HTMLElement>(selector) : null;
  };

  for (const selector of KNOWN_SCROLLABLE_SELECTORS) {
    if (resetSet.has(selector)) continue;
    const el = queryTarget(selector);
    if (el) {
      if (isFreshModalOpen && isInsideModalScope(el)) continue;
      const snap = { top: el.scrollTop, left: el.scrollLeft };
      bySelector.set(selector, snap);
      directElements.set(el, snap);
      if (el.id) {
        byId.set(el.id, snap);
      }
    }
  }

  // 4. Snapshot any element with an [id] inside scopeEl that is currently scrolled or is a <details>
  if (!isFreshModalOpen && initialHostEl && 'querySelectorAll' in initialHostEl) {
    const idNodes = initialHostEl.querySelectorAll<HTMLElement>('[id]');
    idNodes.forEach((node) => {
      if (!node.id || resetSet.has(`#${node.id}`)) return;
      if (node.scrollTop > 0 || node.scrollLeft > 0) {
        byId.set(node.id, { top: node.scrollTop, left: node.scrollLeft });
      }
      if (node.tagName === 'DETAILS') {
        detailsOpenById.set(
          node.id,
          (node as HTMLDetailsElement).open || node.hasAttribute('open')
        );
      }
    });
  }

  if (doc) {
    doc.querySelectorAll<HTMLDetailsElement>('details[id]').forEach((det) => {
      if (det.id && !detailsOpenById.has(det.id)) {
        if (isFreshModalOpen && isInsideModalScope(det)) return;
        detailsOpenById.set(det.id, det.open || det.hasAttribute('open'));
      }
    });
  }

  const hadOpenModalCard = isFreshModalOpen
    ? false
    : Boolean(
        (initialHostEl &&
          'querySelector' in initialHostEl &&
          initialHostEl.querySelector('.ui-modal-card')) ||
          doc?.querySelector('#modal-container.active .ui-modal-card')
      );

  // 5. Blur focused element inside scopeEl or modal container before innerHTML detaches it
  if (doc?.activeElement) {
    const activeEl = doc.activeElement as HTMLElement;
    const shouldBlur =
      (initialHostEl && initialHostEl.contains(activeEl)) ||
      (isFreshModalOpen && isInsideModalScope(activeEl));
    if (shouldBlur && typeof activeEl.blur === 'function') {
      try {
        activeEl.blur();
      } catch {
        // Ignore blur errors in synthetic DOMs
      }
    }
  }

  // 6. Temporarily lock minHeight on scopeEl (when steady re-rendering) so scrollHeight never collapses to 0
  const lockedHeight = !isFreshModalOpen && initialHostEl ? initialHostEl.offsetHeight : 0;
  const prevMinHeight = initialHostEl ? initialHostEl.style.minHeight : '';
  if (initialHostEl && lockedHeight > 0) {
    initialHostEl.style.minHeight = `${lockedHeight}px`;
  }

  try {
    mutateFn();
  } finally {
    if (initialHostEl && lockedHeight > 0) {
      initialHostEl.style.minHeight = prevMinHeight;
    }
  }

  const liveScopeAfter = getLiveScopeEl();

  // 7. Restore <details id="..."> open state before measuring/restoring scrollTop
  for (const [detId, wasOpen] of detailsOpenById.entries()) {
    const detEl =
      (doc ? doc.getElementById(detId) : null) ||
      (liveScopeAfter ? liveScopeAfter.querySelector<HTMLDetailsElement>(`#${detId}`) : null);
    if (detEl) {
      (detEl as HTMLDetailsElement).open = wasOpen;
      if (wasOpen) {
        detEl.setAttribute('open', '');
      } else {
        detEl.removeAttribute('open');
      }
    }
  }

  // 8. Handle modal card steady class & fresh-open zero scroll initialization
  if (isFreshModalOpen) {
    const freshCard = queryTarget('.ui-modal-card');
    if (freshCard) {
      freshCard.classList.remove('ui-modal-card--steady');
      freshCard.scrollTop = 0;
      freshCard.scrollLeft = 0;
      freshCard.querySelectorAll<HTMLElement>('*').forEach((child) => {
        if (child.scrollTop > 0) child.scrollTop = 0;
        if (child.scrollLeft > 0) child.scrollLeft = 0;
      });
    }
  } else if (hadOpenModalCard) {
    const modalCard = queryTarget('.ui-modal-card');
    if (modalCard) {
      modalCard.classList.add('ui-modal-card--steady');
    }
  }

  // Resolved post-mutation elements for safe rAF restoration (prevents touching a different modal opened later)
  const resolvedTargets = new Map<HTMLElement, ScrollSnapshotEntry>();

  const restoreSynchronously = () => {
    for (const [selector, snap] of bySelector.entries()) {
      const el = queryTarget(selector);
      if (!el || (isFreshModalOpen && isInsideModalScope(el))) continue;
      el.scrollTop = snap.top;
      el.scrollLeft = snap.left;
      resolvedTargets.set(el, snap);
    }

    for (const [id, snap] of byId.entries()) {
      const el =
        (doc ? doc.getElementById(id) : null) ||
        (liveScopeAfter ? liveScopeAfter.querySelector<HTMLElement>(`#${id}`) : null);
      if (!el || (isFreshModalOpen && isInsideModalScope(el))) continue;
      el.scrollTop = snap.top;
      el.scrollLeft = snap.left;
      resolvedTargets.set(el, snap);
    }

    for (const [el, snap] of directElements.entries()) {
      if (el.isConnected === false || (isFreshModalOpen && isInsideModalScope(el))) continue;
      el.scrollTop = snap.top;
      el.scrollLeft = snap.left;
      resolvedTargets.set(el, snap);
    }

    const targetDocTop = docElTop || winY;
    const targetDocLeft = docElLeft || winX;

    if (doc?.documentElement) {
      doc.documentElement.scrollTop = targetDocTop;
      doc.documentElement.scrollLeft = targetDocLeft;
    }

    if (doc?.body) {
      doc.body.scrollTop = bodyTop;
      doc.body.scrollLeft = bodyLeft;
    }

    if (doc?.scrollingElement) {
      doc.scrollingElement.scrollTop = scrollingElTop || targetDocTop;
      doc.scrollingElement.scrollLeft = scrollingElLeft || targetDocLeft;
    }

    if (win && typeof win.scrollTo === 'function') {
      const curWinY = win.scrollY || win.pageYOffset || 0;
      const curWinX = win.scrollX || win.pageXOffset || 0;
      if (curWinY !== winY || curWinX !== winX) {
        try {
          win.scrollTo(winX, winY);
        } catch {
          // Ignore in synthetic environments without window.scrollTo implementation
        }
      }
    }
  };

  const restorePostFrame = () => {
    for (const [el, snap] of resolvedTargets.entries()) {
      if (el.isConnected === false) continue;
      if (snap.top > 0 && el.scrollTop === 0) {
        el.scrollTop = snap.top;
      }
      if (snap.left > 0 && el.scrollLeft === 0) {
        el.scrollLeft = snap.left;
      }
    }

    const targetDocTop = docElTop || winY;
    const targetDocLeft = docElLeft || winX;

    if (doc?.documentElement) {
      if (targetDocTop > 0 && doc.documentElement.scrollTop === 0) {
        doc.documentElement.scrollTop = targetDocTop;
      }
      if (targetDocLeft > 0 && doc.documentElement.scrollLeft === 0) {
        doc.documentElement.scrollLeft = targetDocLeft;
      }
    }

    if (doc?.body) {
      if (bodyTop > 0 && doc.body.scrollTop === 0) {
        doc.body.scrollTop = bodyTop;
      }
      if (bodyLeft > 0 && doc.body.scrollLeft === 0) {
        doc.body.scrollLeft = bodyLeft;
      }
    }

    if (doc?.scrollingElement) {
      const targetScrollingTop = scrollingElTop || targetDocTop;
      const targetScrollingLeft = scrollingElLeft || targetDocLeft;
      if (targetScrollingTop > 0 && doc.scrollingElement.scrollTop === 0) {
        doc.scrollingElement.scrollTop = targetScrollingTop;
      }
      if (targetScrollingLeft > 0 && doc.scrollingElement.scrollLeft === 0) {
        doc.scrollingElement.scrollLeft = targetScrollingLeft;
      }
    }

    if (win && typeof win.scrollTo === 'function') {
      const curWinY = win.scrollY || win.pageYOffset || 0;
      const curWinX = win.scrollX || win.pageXOffset || 0;
      if ((winY > 0 && curWinY === 0) || (winX > 0 && curWinX === 0)) {
        try {
          win.scrollTo(winX, winY);
        } catch {
          // Ignore in synthetic environments without window.scrollTo implementation
        }
      }
    }
  };

  // Restore synchronously so layout and tests observe zero scroll jump
  restoreSynchronously();

  // Guard against asynchronous browser layout / native select close clamping on the next frame
  if (win && typeof win.requestAnimationFrame === 'function') {
    const rafId = win.requestAnimationFrame(() => {
      pendingRafEntries = pendingRafEntries.filter((entry) => entry.id !== rafId);
      restorePostFrame();
    });
    pendingRafEntries.push({ win, id: rafId });
  }
}

const focusScrollGuardedElements = new WeakSet<Element>();

/**
 * Prevents mousedown focus-scroll on buttons and interactive controls (`[role="button"]`,
 * `[data-select-pilot]`) inside 3D-transformed or scrollable containers while keeping
 * click and keyboard activation intact.
 */
export function preventButtonFocusScroll(rootEl: ParentNode | null | undefined): void {
  if (!rootEl || typeof rootEl.querySelectorAll !== 'function') return;
  rootEl
    .querySelectorAll<HTMLElement>('button, [role="button"], [data-select-pilot]')
    .forEach((el) => {
      if (el.tagName === 'BUTTON' && !el.hasAttribute('type')) {
        el.setAttribute('type', 'button');
      }
      if (focusScrollGuardedElements.has(el)) return;
      focusScrollGuardedElements.add(el);
      el.addEventListener('mousedown', (ev: MouseEvent) => {
        // Prevent browser from scrolling container to bring element into focus rect on mousedown
        ev.preventDefault();
      });
    });
}

